import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, LinearProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import HorizontalSplitIcon from '@mui/icons-material/HorizontalSplit';

import { ReactComponent as SparkIcon } from '../../../assets/icons/Spark.svg';
import { AskSyntiroContext } from '../../../context/AskSyntiroContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { GroupContext } from '../../../context/GroupContext';
import { useAskSyntiro } from '../hooks/useAskSyntiro';
import AskSyntiroComposer from './AskSyntiroComposer';
import AskSyntiroTurn, { AskSyntiroPendingTurn } from './AskSyntiroTurn';
import {
  BORDER_HAIRLINE,
  ERROR_BG,
  ERROR_BORDER,
  ERROR_TEXT,
  PANEL_GRADIENT,
  SKY,
  SPARK_GRADIENT,
  TEXT_MUTED,
  TEXT_MUTED_DIM,
  TEXT_PRIMARY
} from './askSyntiroTheme';

const PANEL_WIDTH = 384;
const BOTTOM_PANEL_HEIGHT = '45%';
const BOTTOM_PANEL_MIN_HEIGHT = 360;
const TRANSCRIPT_MAX_WIDTH = 860;

export type AskSyntiroPanelPosition = 'right' | 'bottom';
export const ASK_SYNTIRO_POSITION_STORAGE_KEY = 'askSyntiro.panelPosition';

const EMPTY_STATE_MESSAGE =
  "Ask Syntiro about your company's data. On a report, questions are answered from the data on screen.";

/**
 * Viewport-fixed overlay panel, mounted app-wide from `AppBase`, independent
 * of any particular report canvas or dashboard route. Owns `useAskSyntiro`
 * directly — there is no separate container component. Mounted always,
 * visibility toggled via `display`, so the transcript and any in-flight
 * request survive close/reopen. Visual language (dark teal/sky gradient,
 * sparkle badge) matches the retired `forecasting_risk/AskSyntiro` demo —
 * re-derived independently, not imported, so that demo can be deleted at
 * AIC-8 without touching this component.
 * @returns {JSX.Element} The Ask Syntiro panel.
 */
export default function AskSyntiroPanel(): JSX.Element | null {
  const {
    askSyntiroEnabled,
    askSyntiroOpen: isOpen,
    closeAskSyntiro: onClose
  } = useContext(AskSyntiroContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);
  const isTenantReady = Boolean(group?.id && company?.id);

  const {
    turns,
    isAwaitingReply,
    errorMessage,
    sendMessage: onSendMessage,
    retryLastTurn: onRetryLastTurn,
    stopTurn: onStopTurn
  } = useAskSyntiro();

  const transcriptRef = useRef<HTMLDivElement>(null);
  const composerInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [panelPosition, setPanelPosition] = useState<AskSyntiroPanelPosition>(() => {
    try {
      return window.localStorage.getItem(ASK_SYNTIRO_POSITION_STORAGE_KEY) === 'bottom'
        ? 'bottom'
        : 'right';
    } catch {
      return 'right';
    }
  });

  useEffect(() => {
    if (isOpen) {
      composerInputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const container = transcriptRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [turns.length, isAwaitingReply]);

  useEffect(() => {
    if (!isAwaitingReply) {
      setElapsedSeconds(0);
      return undefined;
    }
    const intervalId = setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isAwaitingReply]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !event.isComposing) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleTogglePosition = (): void => {
    const nextPosition: AskSyntiroPanelPosition = panelPosition === 'right' ? 'bottom' : 'right';
    setPanelPosition(nextPosition);
    try {
      window.localStorage.setItem(ASK_SYNTIRO_POSITION_STORAGE_KEY, nextPosition);
    } catch {
      // Best-effort persistence only.
    }
  };

  const centeredContentSx =
    panelPosition === 'bottom'
      ? { maxWidth: TRANSCRIPT_MAX_WIDTH, width: '100%', mx: 'auto' }
      : undefined;

  if (!askSyntiroEnabled) {
    return null;
  }

  return (
    <Box
      aria-busy={isAwaitingReply}
      aria-hidden={!isOpen}
      syntiro-screen-capture="exclude"
      data-ask-syntiro-panel="true"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        zIndex: (theme) => theme.zIndex.drawer - 1,
        overflow: 'hidden',
        background: PANEL_GRADIENT,
        visibility: isOpen ? 'visible' : 'hidden',
        pointerEvents: isOpen ? 'auto' : 'none',
        transform:
          panelPosition === 'right'
            ? `translateX(${isOpen ? '0' : '100%'})`
            : `translateY(${isOpen ? '0' : '100%'})`,
        transition: isOpen
          ? 'transform 300ms ease'
          : 'transform 300ms ease, visibility 0s linear 300ms',
        ...(panelPosition === 'right'
          ? {
              top: 64,
              bottom: 0,
              right: 0,
              width: PANEL_WIDTH,
              borderLeft: '1px solid',
              borderColor: BORDER_HAIRLINE
            }
          : {
              left: 0,
              right: 0,
              bottom: 0,
              height: BOTTOM_PANEL_HEIGHT,
              minHeight: BOTTOM_PANEL_MIN_HEIGHT,
              width: 'auto',
              borderTop: '1px solid',
              borderColor: BORDER_HAIRLINE
            })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          flexShrink: 0,
          px: 2.5,
          py: 1.75,
          borderBottom: '1px solid',
          borderColor: BORDER_HAIRLINE
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            background: SPARK_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#04282d',
            flexShrink: 0
          }}
        >
          <SparkIcon width={16} height={16} />
        </Box>
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700, color: TEXT_PRIMARY }}>
          Ask Syntiro
        </Typography>
        <Box
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: SKY,
            border: '1px solid',
            borderColor: SKY,
            borderRadius: '999px',
            px: 0.75,
            py: 0.125
          }}
        >
          alpha
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          onClick={handleTogglePosition}
          aria-label={
            panelPosition === 'bottom'
              ? 'Dock Ask Syntiro to the right'
              : 'Dock Ask Syntiro to the bottom'
          }
          title={
            panelPosition === 'bottom'
              ? 'Dock Ask Syntiro to the right'
              : 'Dock Ask Syntiro to the bottom'
          }
          sx={{ p: 0.5, color: TEXT_MUTED_DIM, '&:hover': { color: TEXT_PRIMARY } }}
        >
          {panelPosition === 'bottom' ? (
            <VerticalSplitIcon sx={{ fontSize: 16 }} />
          ) : (
            <HorizontalSplitIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close Ask Syntiro"
          sx={{ p: 0.5, color: TEXT_MUTED_DIM, '&:hover': { color: TEXT_PRIMARY } }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Reserved 1px lane, so the progress hairline never shifts the transcript. */}
      <Box sx={{ flexShrink: 0, height: '1px' }}>
        {isAwaitingReply && (
          <LinearProgress
            sx={{
              height: '1px',
              backgroundColor: 'transparent',
              '& .MuiLinearProgress-bar': { backgroundColor: SKY }
            }}
          />
        )}
      </Box>

      <Box
        ref={transcriptRef}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          py: 2,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderRadius: 4
          }
        }}
      >
        <Box sx={centeredContentSx}>
          {turns.length === 0 && !isAwaitingReply && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                px: 3,
                py: 2
              }}
            >
              <Typography
                component="h3"
                sx={{ fontSize: 18, fontWeight: 800, color: TEXT_PRIMARY, mb: 1 }}
              >
                Ask Syntiro
              </Typography>
              <Typography
                component="p"
                sx={{ fontSize: 13, lineHeight: 1.55, color: TEXT_MUTED, maxWidth: 280 }}
              >
                {EMPTY_STATE_MESSAGE}
              </Typography>
            </Box>
          )}

          {turns.map((turn) => (
            <AskSyntiroTurn key={turn.id} turn={turn} />
          ))}

          {isAwaitingReply && <AskSyntiroPendingTurn elapsedSeconds={elapsedSeconds} />}

          {errorMessage !== null && (
            <Box sx={{ px: 2, py: 0.5 }}>
              <Box
                sx={{
                  backgroundColor: ERROR_BG,
                  border: '1px solid',
                  borderColor: ERROR_BORDER,
                  borderRadius: '10px',
                  px: 1.5,
                  py: 1
                }}
              >
                <Typography
                  component="p"
                  sx={{ textAlign: 'left', fontSize: 13, color: ERROR_TEXT, mb: 0.5 }}
                >
                  {errorMessage}
                </Typography>
                <Button
                  onClick={() => void onRetryLastTurn()}
                  sx={{
                    minWidth: 0,
                    p: 0,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: SKY
                  }}
                >
                  Try again
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={centeredContentSx}>
        <AskSyntiroComposer
          onSubmit={(message) => void onSendMessage(message)}
          disabled={!isTenantReady}
          isAwaitingReply={isAwaitingReply}
          onStop={onStopTurn}
          placeholder={isTenantReady ? undefined : 'Preparing Syntiro...'}
          inputRef={composerInputRef}
        />
      </Box>
    </Box>
  );
}
